import { useEffect, useCallback, RefObject } from 'react';
import { useAppBarScrollContext } from './AppBarScrollContext';
import { useIsFloatingMenuVisible } from '../FloatingBottomArea/FloatingMenu';

interface IUseRegisterScrollTargetOptions {
  /** Ref to the container element to search within */
  containerRef: RefObject<HTMLElement | null>;
  /** CSS selector for the scrollable element inside the container */
  selector: string;
  /** Whether scroll-target registration is allowed (e.g. no detail selected) */
  enabled: boolean;
}

/**
 * Registers the scrollable element (found via selector inside containerRef)
 * with AppBarScrollContext so the app bar hides on scroll.
 * Only activates on touch devices with the floating menu enabled,
 * and only when `enabled` is true (typically when no detail is selected).
 */
const useRegisterScrollTarget = ({
  containerRef,
  selector,
  enabled,
}: IUseRegisterScrollTargetOptions): void => {
  const { setScrollTarget } = useAppBarScrollContext();
  const isFloatingMenuVisible = useIsFloatingMenuVisible();

  const shouldRegister = isFloatingMenuVisible && enabled;

  const findScrollElement = useCallback(
    (root: HTMLElement): HTMLElement | null => {
      return root.querySelector(selector) as HTMLElement | null;
    },
    [selector]
  );

  useEffect(() => {
    if (!shouldRegister) {
      setScrollTarget(null);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let currentTarget: HTMLElement | null = null;

    const updateTarget = (): void => {
      if (currentTarget?.isConnected) return;

      const element = findScrollElement(container);
      if (element && element !== currentTarget) {
        currentTarget = element;
        setScrollTarget(element);
      }
    };

    // Initial lookup (element may not exist yet if rendered lazily)
    const element = findScrollElement(container);
    if (element) {
      currentTarget = element;
      setScrollTarget(element);
    }

    const observer = new MutationObserver(() => updateTarget());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      setScrollTarget(null);
    };
  }, [shouldRegister, setScrollTarget, findScrollElement, containerRef]);
};

export default useRegisterScrollTarget;
